import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from './cn';

/**
 * A checkbox.
 *
 * Radix's rather than `<input type="checkbox">` for the indeterminate state:
 * `checked="indeterminate"` is a real value here, where the native element
 * needs an imperative `el.indeterminate = true` that React cannot express as a
 * prop and that is lost on every re-render. The "select all" box at the head of
 * a roster needs exactly that third state.
 *
 * Renders a real `<button role="checkbox">`, so it is focusable and
 * Space-togglable. Pair it with `Label htmlFor` — the box alone names nothing.
 *
 * On a coarse pointer an invisible `::after` grows the hit area to
 * `--tap-min` (44px, WCAG 2.5.8) without growing the box: a pseudo-element is
 * part of its button for hit-testing, so a thumb landing near the box still
 * toggles it. Needed wherever a box stands without a label beside it — the
 * per-row select on a list, where there is no text to tap instead.
 */
export const Checkbox = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(function Checkbox({ className, ...rest }, ref) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'peer relative size-4 shrink-0 rounded-[var(--radius-xs)] border',
        'coarse:after:absolute coarse:after:inset-[calc((100%-var(--tap-min))/2)]',
        'border-[color:var(--border-strong)] bg-[var(--surface-elevated)]',
        'transition-colors duration-[var(--duration-fast)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-[color:var(--brand-700)] data-[state=checked]:bg-[var(--brand-700)]',
        'data-[state=checked]:text-[color:var(--text-on-brand)]',
        'data-[state=indeterminate]:border-[color:var(--brand-700)]',
        'data-[state=indeterminate]:bg-[var(--brand-700)]',
        'data-[state=indeterminate]:text-[color:var(--text-on-brand)]',
        className,
      )}
      {...rest}
    >
      <CheckboxPrimitive.Indicator className="grid place-items-center text-current">
        {rest.checked === 'indeterminate' ? (
          <Minus aria-hidden="true" className="size-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden="true" className="size-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
